/* ==========================================================================
   Rook UI Core — Drawer Controller
   Web Component: <rk-drawer-element>
   Hydration tier: 'interaction' (external trigger pattern)
   ========================================================================== */

import { RkComponent } from '@rook/component';

function waitForExitAnimation(el, done) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!el || reduce) {
        done();
        return () => {};
    }

    let settled = false;
    const onEnd = (e) => {
        if (e.target !== el) return;
        finish();
    };
    const finish = () => {
        if (settled) return;
        settled = true;
        el.removeEventListener('transitionend', onEnd);
        clearTimeout(timer);
        done();
    };
    const cancel = () => {
        if (settled) return;
        settled = true;
        el.removeEventListener('transitionend', onEnd);
        clearTimeout(timer);
    };
    el.addEventListener('transitionend', onEnd);
    const timer = setTimeout(finish, 500);
    return cancel;
}

class RkDrawerElement extends RkComponent {
    static hydration = 'interaction';

    #dragging = false;
    #startY = 0;
    #startX = 0;
    #currentTranslate = 0;
    #dismissThreshold = 0.3;
    #cancelExitAnim = null;
    #keydownController = null;
    #dragController = null;
    #triggerCleanups = [];

    attachHydrationTrigger(hydrate) {
        const drawer = this.querySelector('.rk-drawer');
        const id = this.dataset.drawerId || drawer?.id;
        if (!id) return () => {};

        const triggers = document.querySelectorAll(`[data-drawer-open="${id}"]`);
        const cleanups = [];

        triggers.forEach((trigger) => {
            const onClick = (event) => {
                event.preventDefault();
                trigger.removeEventListener('click', onClick);
                hydrate();
                this.open();
            };
            trigger.addEventListener('click', onClick);
            cleanups.push(() => trigger.removeEventListener('click', onClick));
        });

        return () => cleanups.forEach((fn) => fn());
    }

    setup() {
        this.drawer = this.querySelector('.rk-drawer');
        this.overlay = this.querySelector('.rk-drawer__overlay');
        this.panel = this.querySelector('.rk-drawer__panel');
        this.handle = this.querySelector('.rk-drawer__handle');
        this.previousFocus = null;
        this.focusableElements = [];

        if (!this.drawer || !this.panel) return;

        const delegateMap = {
            'click [data-action="close"]': () => this.close(),
            'pointerdown .rk-drawer__handle': (event) => this.#onPointerDown(event),
        };
        if (this.overlay) {
            delegateMap['click .rk-drawer__overlay'] = (event, target) => {
                if (event.target === target) this.close();
            };
        }
        this.delegate(delegateMap);

        this.#setupPersistentOpenTriggers();
    }

    teardown() {
        if (this.#cancelExitAnim) {
            this.#cancelExitAnim();
            this.#cancelExitAnim = null;
        }
        if (this.#keydownController) {
            this.#keydownController.abort();
            this.#keydownController = null;
        }
        if (this.#dragController) {
            this.#dragController.abort();
            this.#dragController = null;
        }
        this.#triggerCleanups.forEach((fn) => fn());
        this.#triggerCleanups = [];
    }

    #setupPersistentOpenTriggers() {
        const drawerId = this.dataset.drawerId || this.drawer?.id;
        if (!drawerId) return;

        const triggers = document.querySelectorAll(`[data-drawer-open="${drawerId}"]`);
        triggers.forEach((trigger) => {
            const onClick = (e) => {
                e.preventDefault();
                this.open();
            };
            trigger.addEventListener('click', onClick);
            this.#triggerCleanups.push(() => trigger.removeEventListener('click', onClick));
        });
    }

    isOpen() {
        return this.drawer?.classList.contains('rk-drawer--active');
    }

    open() {
        if (!this.drawer) return;

        if (this.#cancelExitAnim) {
            this.#cancelExitAnim();
            this.#cancelExitAnim = null;
        }

        if (this.isOpen() && this.drawer.dataset.state !== 'closed') return;

        this.previousFocus = document.activeElement;
        this.drawer.classList.add('rk-drawer--active');
        this.drawer.dataset.state = 'open';
        document.body.style.overflow = 'hidden';

        this.#keydownController?.abort();
        this.#keydownController = new AbortController();
        document.addEventListener('keydown', (e) => this.#onKeyDown(e), { signal: this.#keydownController.signal });

        this.panel.style.transform = '';
        this.panel.style.transition = '';

        requestAnimationFrame(() => {
            this.focusableElements = this.panel.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (this.focusableElements.length > 0) {
                this.focusableElements[0].focus();
            }
        });

        this.rkEmit('drawer:open', { id: this.drawer.id });
    }

    close() {
        if (!this.drawer || !this.isOpen()) return;
        if (this.drawer.dataset.state === 'closed') return;

        this.drawer.dataset.state = 'closed';

        this.panel.style.transform = '';
        this.panel.style.transition = '';

        const finalize = () => {
            this.#cancelExitAnim = null;
            this.drawer.classList.remove('rk-drawer--active');
            delete this.drawer.dataset.state;
            document.body.style.overflow = '';

            this.#keydownController?.abort();
            this.#keydownController = null;

            if (this.previousFocus) {
                this.previousFocus.focus();
                this.previousFocus = null;
            }

            this.rkEmit('drawer:close', { id: this.drawer.id });
        };

        this.#cancelExitAnim = waitForExitAnimation(this.panel, finalize);
    }

    #onKeyDown(e) {
        if (e.key === 'Escape' && this.isOpen()) {
            this.close();
        }
    }

    #getSide() {
        return this.dataset.side || 'bottom';
    }

    #onPointerDown(e) {
        if (!this.isOpen()) return;
        e.preventDefault();

        this.#dragging = true;
        this.#startY = e.clientY;
        this.#startX = e.clientX;
        this.#currentTranslate = 0;
        this.panel.style.transition = 'none';

        this.#dragController?.abort();
        this.#dragController = new AbortController();
        const signal = this.#dragController.signal;

        document.addEventListener('pointermove', (ev) => this.#onPointerMove(ev), { signal });
        document.addEventListener('pointerup', () => this.#onPointerUp(), { signal });
    }

    #onPointerMove(e) {
        if (!this.#dragging) return;

        const side = this.#getSide();
        let delta;

        switch (side) {
            case 'bottom':
                delta = Math.max(0, e.clientY - this.#startY);
                this.panel.style.transform = `translateY(${delta}px)`;
                break;
            case 'top':
                delta = Math.min(0, e.clientY - this.#startY);
                this.panel.style.transform = `translateY(${delta}px)`;
                break;
            case 'right':
                delta = Math.max(0, e.clientX - this.#startX);
                this.panel.style.transform = `translateX(${delta}px)`;
                break;
            case 'left':
                delta = Math.min(0, e.clientX - this.#startX);
                this.panel.style.transform = `translateX(${delta}px)`;
                break;
        }

        this.#currentTranslate = Math.abs(delta);
    }

    #onPointerUp() {
        if (!this.#dragging) return;
        this.#dragging = false;

        this.#dragController?.abort();
        this.#dragController = null;

        const side = this.#getSide();
        const isVertical = side === 'top' || side === 'bottom';
        const panelSize = isVertical ? this.panel.offsetHeight : this.panel.offsetWidth;

        this.panel.style.transition = '';

        if (this.#currentTranslate > panelSize * this.#dismissThreshold) {
            this.close();
        } else {
            this.panel.style.transform = '';
        }
    }
}

if (!customElements.get('rk-drawer-element')) {
    customElements.define('rk-drawer-element', RkDrawerElement);
}
